import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  useAsyncEffect,
  useJanitor,
  useMount,
} from '../../hooks';
import Router from '../../../../packages/router';
import FeatureFlags from '../../modules/FeatureFlags';
import { setMeta } from '../../modules/PageMeta';
import type { IRootDispatch, RootState } from '../../store/models';
import { SITE_ROOT } from '../../config';

import { useShouldShowClientNavBar } from '../consumer/ClientNavBar/hooks';

import ResponsiveLayout from '../layouts/ResponsiveLayout';
import { HomePage } from '../shared/homepage/HomePage';
import { PROMO_MOD_FLAG } from '../shared/homepage/promotions/constants';

import LOGO from '../../../images/styleseat-logo-black.svg';
import { AppointmentOverviewToday, loadAppointmentsToday } from '../../api/Users/Appointments/AppointmentsToday';
import { AppointmentDetailsStatus } from '../../api/Users/Appointments/AppointmentsStatusDetails';
import { filterTodayAppointments } from '../../modules/consumer/appointments';
import useCheckForDiscounts from '../consumer/incentives/useCheckForDiscounts';
import { getIsApp } from '../../modules/AppInfo';
import { markDeepLinkingReady } from '../../modules/appsFlyer';
import { SEARCH_ROUTE } from '../../route-names';

/**
 * Renders the full homepage
 */
export const Home: React.FCWithChildren = () => {
  const [upcomingAppt, setUpcomingAppt] = useState<AppointmentOverviewToday | null>(null);
  const [currentAppt, setCurrentAppt] = useState<AppointmentOverviewToday | null>(null);
  const [isPromoModEnabled, setIsPromoModEnabled] = useState<boolean>();
  const {
    shouldShowClientNav,
    loading: loadingNavBarState,
  } = useShouldShowClientNavBar();

  const isLoggedIn = useSelector<RootState, boolean>(({ user }) => !user.is_anon);
  const userId = useSelector<RootState, number>(({ user }) => (user.is_anon ? null : user.user_id));
  const routeParams = useSelector<RootState, Record<string, string>>(
    ({ route }) => route.params,
  );
  const dispatch = useDispatch<IRootDispatch>();
  const janitor = useJanitor();

  // Note: Because other incentives/discount components rely on the store being populated correctly,
  // this dispatch needs to be called for home page landers.
  useCheckForDiscounts();

  const setNextAppointmentToday = async (inputUserId: number) => {
    if (!inputUserId) {
      setUpcomingAppt(null);
      setCurrentAppt(null);
      return;
    }

    const apptsToday = await loadAppointmentsToday(inputUserId).then(filterTodayAppointments);
    setCurrentAppt(apptsToday[AppointmentDetailsStatus.Current]);
    setUpcomingAppt(apptsToday[AppointmentDetailsStatus.Upcoming]);
  };

  useMount(async () => {
    // The dataLayer event requires a slight delay to run properly
    setTimeout(() => {
      (window as any).dataLayer.push({ event: 'gtm.load' });
    }, 100);

    if (!isLoggedIn) {
      dispatch.loader.setIsLoading(false);
    }

    dispatch.clientNavBar.onNavigatedToHome();
    dispatch.favorites.onNavigatedToHome();

    setMeta({
      canonical: `${SITE_ROOT}/m/`,
      image: LOGO,
      title: 'Hair Appointments | Beauty Salons: Book Online | StyleSeat',
    });

    await janitor.addPromise(FeatureFlags.getFlags());

    if (getIsApp() && isLoggedIn) {
      markDeepLinkingReady();
    }

    dispatch.loader.setIsLoading(false);

    const promoModEnabled = await janitor.addPromise(FeatureFlags.isEnabled(PROMO_MOD_FLAG));

    setIsPromoModEnabled(promoModEnabled);
  });

  useAsyncEffect(async () => {
    if (loadingNavBarState) {
      return;
    }
    // Resets history service so that the "back arrow" doesn't
    // display on the header after someone lands on the home page
    Router.history.reset();
  }, [
    shouldShowClientNav,
    loadingNavBarState,
  ]);

  useEffect(() => {
    setNextAppointmentToday(userId);
  }, [userId]);

  const handleIncentivesSearch = (_, __, slugged) => {
    dispatch.route.go({
      route: SEARCH_ROUTE,
      params: slugged,
      notify: true,
    });
  };

  const utmParams = {
    utm_source: routeParams.utm_source,
    utm_campaign: routeParams.utm_campaign,
    utm_content: routeParams.utm_content,
    utm_medium: routeParams.utm_medium,
    utm_term: routeParams.utm_term,
  };

  return (
    <ResponsiveLayout
      showClientNavBar={shouldShowClientNav}
    >
      <HomePage
        currentAppt={currentAppt}
        isLoggedIn={isLoggedIn}
        isPromoModEnabled={isPromoModEnabled}
        onIncentivesSearch={handleIncentivesSearch}
        upcomingAppt={upcomingAppt}
        utmParams={utmParams}
      />
    </ResponsiveLayout>
  );
};

export default Home;
